import type { Node as ProseMirrorNode } from "prosemirror-model";
import { TextSelection, type EditorState, type Transaction } from "prosemirror-state";

import type { FoundNode } from "../schema";

/**
 * Move a node one place among its siblings, keeping the caret inside it. Used
 * by both the block handle (`Mod-Alt-↑`/`↓`) and list-item movement
 * (`Alt-↑`/`↓`), which differ only in which ancestor they pick.
 */
export function swapWithSibling(
  state: EditorState,
  found: FoundNode,
  direction: -1 | 1,
  dispatch?: (tr: Transaction) => void,
): boolean {
  const $pos = state.doc.resolve(found.pos);
  const parent = $pos.parent;
  const index = $pos.index();
  const target = index + direction;
  if (target < 0 || target >= parent.childCount) return false;
  if (dispatch) {
    const node: ProseMirrorNode = parent.child(index);
    const neighbour: ProseMirrorNode = parent.child(target);
    const offsetInNode = state.selection.from - found.pos;
    const tr = state.tr.delete(found.pos, found.pos + node.nodeSize);
    const insertAt =
      direction === -1 ? found.pos - neighbour.nodeSize : found.pos + neighbour.nodeSize;
    tr.insert(insertAt, node);
    const caret = Math.min(insertAt + offsetInNode, tr.doc.content.size);
    tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
    dispatch(tr.scrollIntoView());
  }
  return true;
}
